import { NumberInputField } from "@src/components/deprecated/NumberInputField/NumberInputField";
import { Button } from "@src/components/ui-components/Buttons/Button/Button";
import { usePingedCloudFunction } from "@src/data-hooks/generic/usePingedCloudFunction";
import { getFunctions, httpsCallable } from "firebase/functions";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { ModalPopup } from "../../../components/deprecated/ModalPopup/ModalPopup";
import { SelectInputData, SelectInputField } from "../../../components/deprecated/SelectInputField/SelectInputField";
import { Text } from "../../../components/deprecated/Text/Text";
import { TextInputField } from "../../../components/deprecated/TextInputField/TextInputField";
import { ToastUtilities } from "../../../libs/react-toastify/ToastUtilities";
import { FirebaseInfoField } from "../../../to-be-removed/LocalData/General/FirebaseInfoField";
import { IncomeData, IncomePeriodData } from "../../../to-be-removed/LocalData/Property/Income/IncomeData";
import { UnitData } from "../../../to-be-removed/LocalData/Property/Units/UnitData";
import styles from "./RevenuePopupSettings.module.css";

type RentData = {
  sum?: number;
  quantity?: number;
  description?: string;
  periodType: number;
};

enum RentType {
  AREA = "area",
  PARKING = "parking",
  COMMON = "common",
  OTHER = "other",
}

type RentTypeKey = `${RentType}`;

const options = [
  new SelectInputData("12", t("main.perYear")),
  new SelectInputData("3", t("main.perQuarter")),
  new SelectInputData("1", t("main.perMonth")),
];

type Props = {
  id: string;
  unit: UnitData;
  runAfterSave?: (unit: UnitData) => void;
  runBeforeSave?: (rawData: IncomeData) => void;
  showUnitSettings?: () => void;
};

export const RevenuePopupSettings = ({ id, unit, runAfterSave, runBeforeSave, showUnitSettings }: Props) => {
  const [areaRent, setAreaRent] = useState<RentData[]>([]);
  const [parkingRent, setParkingRent] = useState<RentData[]>([]);
  const [commonRent, setCommonRent] = useState<RentData[]>([]);
  const [otherRent, setOtherRent] = useState<RentData[]>([]);
  const _ = usePingedCloudFunction("updateTenantInfoFields");

  const mapRentData = (incomeDataArray: IncomePeriodData[], includeDescription = true) => {
    return incomeDataArray.map((incomeData) => {
      const { periodType = 12, quantity = 1, sum = 0, description = "" } = incomeData;
      const mappedData: RentData = { periodType, quantity, sum };

      if (includeDescription) {
        mappedData.description = description;
      }

      return mappedData;
    });
  };

  useEffect(() => {
    const incomeData: IncomeData = unit.incomeData?.value ?? new IncomeData();
    setAreaRent(mapRentData(incomeData.areaRent));
    setParkingRent(mapRentData(incomeData.parkingRent, false));
    setCommonRent(mapRentData(incomeData.commonRent));
    setOtherRent(mapRentData(incomeData.otherRent));
  }, [unit]);

  const saveUpdate = () => {
    const unitObject = {
      incomeData: {
        value: {
          areaRent,
          parkingRent,
          commonRent,
          otherRent,
          totalPeriod: 12,
        },
      },
    };

    if (runBeforeSave) {
      const incomeData = new IncomeData();
      incomeData.setup(unitObject.incomeData.value);
      runBeforeSave(incomeData);
      return;
    }

    if (runAfterSave && unit.property !== undefined) {
      const functions = getFunctions(undefined, "europe-west1");
      const updateTenantInfoFields = httpsCallable(functions, "updateTenantInfoFields");

      ToastUtilities.presentSavingToast(
        updateTenantInfoFields({
          propertyID: unit.property.id,
          unitID: unit.id,
          fields: unitObject,
        })
          .then(() => {
            if (unit) {
              unit.incomeData = new FirebaseInfoField(new IncomeData());
              unit.incomeData.value?.setup(unitObject.incomeData.value);
              runAfterSave?.(unit);
              if (showUnitSettings) showUnitSettings();
            }
          })
          .catch((error) => {
            ToastUtilities.presentServerError(error);
          })
      );
    }
  };

  const addRow = (type: RentTypeKey) => {
    if (type === RentType.AREA) {
      setAreaRent((oldAreaRent) => [...oldAreaRent, { periodType: 12, quantity: 1, description: "", sum: 0 }]);
    } else if (type === RentType.PARKING) {
      setParkingRent((oldParkingRent) => [...oldParkingRent, { periodType: 12, quantity: 1, sum: 0 }]);
    } else if (type === RentType.COMMON) {
      setCommonRent((oldCommonRent) => [...oldCommonRent, { periodType: 12, quantity: 1, description: "", sum: 0 }]);
    } else {
      setOtherRent((oldOtherRent) => [...oldOtherRent, { periodType: 12, quantity: 1, description: "", sum: 0 }]);
    }
  };

  const addMoreButtonContent = `+ ${t("main.clickToAddMoreSums")}`;

  const handleInputChange = (type: RentTypeKey, index: number, field: string, value: any, rentData: RentData[]) => {
    const newData = [...rentData];
    newData[index] = { ...newData[index], [field]: value };

    switch (type) {
      case RentType.AREA:
        setAreaRent(newData);
        break;
      case RentType.PARKING:
        setParkingRent(newData);
        break;
      case RentType.COMMON:
        setCommonRent(newData);
        break;
      case RentType.OTHER:
        setOtherRent(newData);
        break;
      default:
        break;
    }
  };

  const renderRentTable = (type: RentTypeKey, rentData: RentData[]) => {
    return (
      <div>
        <Text isBold size="md" marginLeft={10} marginBottom={-10} marginTop={type === RentType.AREA ? 25 : 50}>
          {t(`main.${type}Rent`)}
        </Text>
        <table className={styles.tableContainer}>
          <thead>
            <tr>
              <th>
                <Text marginLeft={10} marginBottom={-5} size="sm">
                  {type === RentType.PARKING ? t("main.parkingSpots") : t("main.description")}
                </Text>
              </th>
              <th>
                <Text marginLeft={10} marginBottom={-5} size="sm">
                  {type === RentType.PARKING ? t("main.pricePerSpot") : t("main.sum")}
                </Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {rentData.map((rent, index) => (
              <tr key={index}>
                {type === RentType.PARKING ? (
                  <>
                    <td>
                      <NumberInputField value={rent.quantity} onChange={(value) => handleInputChange(type, index, "quantity", value, rentData)} />
                    </td>
                  </>
                ) : (
                  <>
                    <td>
                      <TextInputField
                        value={rent.description?.toString()}
                        onChange={(value) => handleInputChange(type, index, "description", value, rentData)}
                      />
                    </td>
                  </>
                )}
                <td>
                  <NumberInputField value={rent.sum} onChange={(value) => handleInputChange(type, index, "sum", value, rentData)} />
                </td>
                <td>
                  <Text size="md">,-</Text>
                </td>
                <td>
                  <SelectInputField
                    width="160px"
                    options={options}
                    value={rent.periodType?.toString()}
                    onChange={(selectedOption) => handleInputChange(type, index, "periodType", parseInt(selectedOption?.value), rentData)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Button size="sm" color="var(--primary-color)" marginTop={-5} marginLeft={10} type="text" onClick={() => addRow(type)}>
          {addMoreButtonContent}
        </Button>
      </div>
    );
  };

  return (
    <ModalPopup
      containerProps={{
        centered: true,
        topMargin: "4vh",
        maxHeight: "92vh",
        width: "auto",
      }}
      id={id}
      title={t("main.administrateRent")}
      onActionClicked={saveUpdate}
      actionTitle={t("main.saveChanges")}
    >
      <div className={styles.pageContainer}>
        {renderRentTable(RentType.AREA, areaRent)}
        {renderRentTable(RentType.PARKING, parkingRent)}
        {renderRentTable(RentType.COMMON, commonRent)}
        {renderRentTable(RentType.OTHER, otherRent)}
      </div>
    </ModalPopup>
  );
};
